/**
 * Session Manager
 * Centralized service for handling session expiration and re-authentication.
 *
 * When silent refresh fails, this tears down local auth state and hard-redirects
 * to the login page. The redirect does not depend on React being mounted, so
 * TokenHandler (which can fire before the authenticated shell) cannot lose the
 * event. Duplicate triggers are ignored until a successful refresh calls reset().
 *
 * This is the single gate for expiry teardown. Callers (401 handler, token
 * monitor, Auth0 errors) must go through handleSessionExpired rather than
 * logging the user out themselves.
 */

import { createFileLogger } from '../utils/logger';
import { parseJwtPayload } from '../utils/jwtUtils';
import { getAuthEntryPath, isAuthEntryPath } from '../config/authRoutes';
import {
  getPinnedOrStoredAccessToken,
  pinSessionTokens,
  reassertPinnedSessionTokens,
} from '../utils/authTokenGuard';
import { dumpAuthDebug, describeToken } from '../utils/authDebug';

const log = createFileLogger('SESSION_MANAGER');

function tokenSub(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    return parseJwtPayload(token)?.sub ?? null;
  } catch {
    return null;
  }
}

class SessionManager {
  private hasHandledExpiry = false;
  private sessionEpoch = 0;
  private auth0LocalLogout: ((params: any) => void) | null = null;

  /**
   * Register the Auth0 SDK logout so expiry can clear leftover `isAuthenticated`
   * state without a federated redirect to marketing `/`.
   */
  registerAuth0LocalLogout(fn: ((params: any) => void) | null): void {
    this.auth0LocalLogout = fn;
  }

  /** Drop leftover Auth0 SDK state without a federated redirect. */
  clearLeftoverAuth0Sdk(): Promise<void> {
    if (!this.auth0LocalLogout) return Promise.resolve();
    try {
      return Promise.resolve(this.auth0LocalLogout({ openUrl: false })).then(
        () => undefined,
      );
    } catch (err) {
      log.warn('Auth0 local logout failed', err);
      return Promise.resolve();
    }
  }

  /**
   * Write login/signup tokens, pin them in memory, bump the session epoch, and
   * drop leftover Auth0 SPA state. Auth0 `logout({ openUrl: false })` is async
   * and can restore `localStorage.authToken` from an old SDK session — the pin
   * plus a reassert after that logout is what keeps API calls on the new JWT.
   */
  adoptSessionTokens(tokens: {
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
  }): void {
    pinSessionTokens(tokens);
    this.beginSession();
    dumpAuthDebug('adoptSessionTokens — session pinned', {
      incomingAccess: describeToken(tokens.access_token),
      incomingId: describeToken(tokens.id_token),
      incomingHasRefresh: !!tokens.refresh_token,
      auth0LocalLogoutRegistered: !!this.auth0LocalLogout,
      epoch: this.sessionEpoch,
    });
    void this.clearLeftoverAuth0Sdk().finally(() => {
      reassertPinnedSessionTokens();
      dumpAuthDebug('adoptSessionTokens — after leftover Auth0 logout', {
        epoch: this.sessionEpoch,
      });
    });
  }

  /**
   * Mark a newly stored login/signup session. In-flight 401s from a previous
   * identity must not tear this session down.
   */
  beginSession(): void {
    this.sessionEpoch += 1;
    this.hasHandledExpiry = false;
    log.log('New session begun', { epoch: this.sessionEpoch });
  }

  getSessionEpoch(): number {
    return this.sessionEpoch;
  }

  /**
   * Tear down the expired session and send the user to the login page.
   *
   * @param failedToken The token that was just rejected / found expired. When
   *   a newer, still-valid token is already in storage (signup or silent
   *   refresh won the race), this is a no-op so we do not destroy the new
   *   session. Omit only when there is no specific failed token (Auth0 SDK
   *   errors with no local JWT yet).
   * @param requestEpoch Session epoch captured when the failing request started.
   *   Mismatched epochs mean signup/login already replaced the session.
   */
  handleSessionExpired(failedToken?: string | null, requestEpoch?: number): void {
    dumpAuthDebug('handleSessionExpired called', {
      hasHandledExpiry: this.hasHandledExpiry,
      requestEpoch: requestEpoch ?? null,
      sessionEpoch: this.sessionEpoch,
      failed: describeToken(failedToken),
      pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    });

    if (this.hasHandledExpiry) {
      log.warn('Session expiry already handled, ignoring duplicate trigger');
      dumpAuthDebug('handleSessionExpired NO-OP: already handled');
      return;
    }

    if (requestEpoch !== undefined && requestEpoch !== this.sessionEpoch) {
      log.log('Ignoring expiry trigger from a previous session epoch', {
        requestEpoch,
        sessionEpoch: this.sessionEpoch,
      });
      dumpAuthDebug('handleSessionExpired NO-OP: stale epoch', {
        requestEpoch,
        sessionEpoch: this.sessionEpoch,
      });
      return;
    }

    const pathname = window.location.pathname;
    const search = window.location.search;

    // Login/signup own token writes. In-flight 401s and leftover Auth0 errors
    // must not delete a token that was just stored on this page.
    if (isAuthEntryPath(pathname)) {
      log.log('Already on an auth page — ignoring expiry trigger', { pathname });
      dumpAuthDebug('handleSessionExpired NO-OP: on auth entry path', { pathname });
      return;
    }

    const current = getPinnedOrStoredAccessToken();
    const currentSub = tokenSub(current);
    const failedSub = tokenSub(failedToken);
    if (current) {
      if (!failedToken && !this.isTokenExpired(current)) {
        log.log('Ignoring expiry trigger with no failed token — a valid session is in storage');
        dumpAuthDebug('handleSessionExpired NO-OP: valid current, no failed token');
        return;
      }
      if (failedToken && current !== failedToken) {
        if (currentSub && failedSub && currentSub !== failedSub) {
          log.log('Current token is a different identity than the failed one — ignoring expiry trigger', {
            currentSub,
            failedSub,
          });
          dumpAuthDebug('handleSessionExpired NO-OP: different identity', {
            currentSub,
            failedSub,
          });
          return;
        }
        if (!this.isTokenExpired(current)) {
          log.log('A newer access token replaced the failed one — ignoring expiry trigger');
          dumpAuthDebug('handleSessionExpired NO-OP: newer unexpired token');
          return;
        }
      }
    }

    this.hasHandledExpiry = true;

    const loginPath = getAuthEntryPath();
    const returnTo = pathname === '/' ? undefined : pathname + search;

    log.warn('Session expired — redirecting to login', { returnTo, loginPath });
    dumpAuthDebug('handleSessionExpired TEARDOWN — redirecting to login', {
      returnTo,
      loginPath,
      currentSub,
      failedSub,
      currentEqualsFailed: current === failedToken,
    });

    const auth0Logout = this.auth0LocalLogout;
    void import('../utils/logoutUtils')
      .then(({ performCompleteLogout }) =>
        performCompleteLogout({
          hasAuth0User: !!auth0Logout,
          auth0Logout: auth0Logout ?? undefined,
          auth0LocalOnly: true,
          redirectTo: loginPath,
          preserveRedirectAfterLogin: returnTo,
        }),
      )
      .catch((err) => {
        log.error('Session expiry logout failed — forcing login redirect', err);
        try {
          if (returnTo) localStorage.setItem('redirectAfterLogin', returnTo);
        } catch {
          /* ignore */
        }
        window.location.href = loginPath;
      });
  }

  /**
   * Reset the session manager after a successful token refresh so a later
   * expiry can be handled again.
   */
  reset(): void {
    log.log('Session manager reset');
    this.hasHandledExpiry = false;
  }

  /**
   * Check if token is expired or about to expire.
   * Returns true if token is expired or will expire within the next minute.
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = parseJwtPayload(token);

      if (!payload || !payload.exp) {
        return true;
      }

      const now = Date.now();
      const expiry = payload.exp * 1000;
      const bufferMs = 60 * 1000; // 1 minute buffer

      return expiry - now < bufferMs;
    } catch (error) {
      log.error('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Get time until token expires (in ms)
   */
  getTimeUntilExpiry(token: string): number | null {
    try {
      const payload = parseJwtPayload(token);

      if (!payload || !payload.exp) {
        return null;
      }

      const now = Date.now();
      const expiry = payload.exp * 1000;

      return Math.max(0, expiry - now);
    } catch (error) {
      log.error('Error calculating time until expiry:', error);
      return null;
    }
  }
}

export const sessionManager = new SessionManager();
export default sessionManager;
